import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import LoadingSpinner from '../ui/LoadingSpinner';

const PrivateRoute = ({ roles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { settings, loading: settingsLoading } = useSettings();

  if (loading || settingsLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    // Redirect to login but save the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles.length > 0 && !roles.includes(user.role)) {
    // User authorized but not for this role
    return <Navigate to="/" replace />;
  }

  const getPageKey = (pathname) => {
    const p = String(pathname || '');
    if (p === '/' || p === '/dashboard') return 'dashboard';
    if (p === '/kanban') return 'efile_my_desk';
    if (p === '/files') return 'efile_workspace';
    if (p.startsWith('/efile/')) return 'efile_workspace';
    if (p === '/record-room') return 'record_room';
    if (p === '/delegations') return 'delegations';
    if (p === '/address-book') return 'address_book';
    if (p === '/dispatch') return 'dispatch_list';
    if (p === '/dispatch/new') return 'dispatch_create';
    if (/^\/dispatch\/[^/]+\/view$/.test(p)) return 'dispatch_preview';
    if (/^\/dispatch\/[^/]+\/print$/.test(p)) return 'dispatch_print';
    if (/^\/dispatch\/[^/]+$/.test(p)) return 'dispatch_edit';
    if (p === '/diary') return 'diary_list';
    if (p === '/diary/new') return 'diary_create';
    if (/^\/diary\/[^/]+\/view$/.test(p)) return 'diary_preview';
    if (/^\/diary\/[^/]+$/.test(p)) return 'diary_edit';
    if (p.startsWith('/green-sheet/')) return 'green_sheet';
    if (p === '/compliance') return 'compliance';
    if (p === '/approvals') return 'approvals';
    if (p === '/search') return 'search';
    if (p === '/reports') return 'reports';
    if (p === '/profile' || p.startsWith('/profile/')) return 'profile';
    if (p === '/notifications') return 'notifications';
    if (p === '/users') return 'user_management';
    if (p === '/audit') return 'audit_trail';
    if (p === '/settings') return 'system_settings';
    if (p === '/offices') return 'office_management';
    return null;
  };

  const access = settings?.role_page_access;
  const normalizeRole = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const resolveRoleKey = (obj, role) => {
    if (!obj || typeof obj !== 'object') return role;
    const r = normalizeRole(role);
    const keys = Object.keys(obj);
    const exact = keys.find(k => String(k).trim() === String(role || '').trim());
    if (exact) return exact;
    const ci = keys.find(k => normalizeRole(k) === r);
    return ci || role;
  };

  const roleKey = resolveRoleKey(access, user.role);
  const allowedRaw = access && typeof access === 'object' ? access[roleKey] : null;
  const allowed = Array.isArray(allowedRaw) ? allowedRaw : (access && typeof access === 'object' ? [] : null);
  const pageKey = getPageKey(location.pathname);
  if (allowed && pageKey && !allowed.includes(pageKey)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

export default PrivateRoute;
